import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { 
    MessageSquarePlus, 
    X, 
    Send, 
    Loader2,
    CheckCircle
} from 'lucide-react';
import { feedbackAPI } from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const FeedbackButton = () => {
    const { user } = useAuth();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    // Don't show on login, signup, or admin login pages
    const excludedPaths = ['/login', '/signup', '/admin/login', '/forgot-password', '/reset-password', '/verify-email'];
    if (!user || excludedPaths.some(path => location.pathname.startsWith(path))) {
        return null;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!message.trim() || message.trim().length < 10) {
            toast.error('Please enter at least 10 characters');
            return;
        }

        setSending(true);
        try {
            await feedbackAPI.submit({
                message: message.trim(),
                page: location.pathname
            });
            setSent(true);
            setMessage('');
            setTimeout(() => {
                setIsOpen(false);
                setSent(false);
            }, 2000);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to send feedback');
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            {/* Floating button */}
            <button
                onClick={() => setIsOpen(true)}
                className={cn(
                    "fixed bottom-20 right-4 z-50 p-3 rounded-full shadow-lg",
                    "bg-primary text-primary-foreground",
                    "hover:scale-110 transition-transform",
                    "md:bottom-6 md:right-6",
                    isOpen && "hidden"
                )}
                aria-label="Send feedback"
                data-testid="feedback-btn"
            >
                <MessageSquarePlus className="h-5 w-5" />
            </button>

            {/* Feedback modal */}
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-end justify-center md:items-center">
                    {/* Backdrop */}
                    <div 
                        className="absolute inset-0 bg-black/50"
                        onClick={() => !sending && setIsOpen(false)}
                    />
                    
                    {/* Modal */}
                    <div className="relative w-full max-w-md mx-4 mb-4 md:mb-0 bg-card rounded-xl shadow-xl border border-border animate-in slide-in-from-bottom-4">
                        <div className="p-4">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-semibold text-foreground">
                                    Report Issue / Suggest Feature
                                </h3>
                                <button 
                                    onClick={() => setIsOpen(false)}
                                    className="p-1 hover:bg-accent rounded-full"
                                    disabled={sending}
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>

                            {sent ? (
                                <div className="py-8 text-center">
                                    <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                                    <p className="font-medium text-foreground">Thank you!</p>
                                    <p className="text-sm text-muted-foreground">Your feedback helps us improve.</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit}>
                                    <Textarea
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        placeholder="Describe the issue or your suggestion..."
                                        className="min-h-[120px] bg-accent border-border resize-none"
                                        maxLength={1000}
                                        disabled={sending}
                                        data-testid="feedback-textarea"
                                    />
                                    <div className="flex items-center justify-between mt-3">
                                        <span className="text-xs text-muted-foreground">
                                            {message.length}/1000
                                        </span>
                                        <Button 
                                            type="submit" 
                                            disabled={sending || message.trim().length < 10}
                                            className="h-10"
                                            data-testid="feedback-submit-btn"
                                        >
                                            {sending ? (
                                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                            ) : (
                                                <Send className="h-4 w-4 mr-2" />
                                            )}
                                            Send Feedback
                                        </Button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default FeedbackButton;
